import { useState } from 'react';

import { Icon } from '~/modules/elements/akui/icon';
import MicAccessDeniedView from '~/modules/user-media/mic-access-denied-view';
import userMediaService from '~/modules/user-media/user-media-service';

interface Props {
  onComplete: () => void;
}

// Reached only when the probe in `ConnectionWizard` found microphone permission denied, so the
// blocked panel is the state this step opens in. The ask-for-it panel is what a retry that is still
// refused falls back to, and what a browser that has not been asked yet would show.
const StepAllowMic = ({ onComplete }: Props) => {
  const [blocked, setBlocked] = useState(true);

  const request = () => {
    userMediaService
      .requestMicPermission()
      .then(onComplete)
      .catch(() => setBlocked(true));
  };

  if (!blocked) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <span className="mt-5.5 text-[1.875rem] leading-[1.1] font-bold text-pretty">
          This phone is the microphone.
        </span>
        <span className="mt-2.5 text-[1rem] leading-[1.45] text-white/85">
          Allow the microphone and sing into it — the game listens over your Wi-Fi.
        </span>
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <span className="flex size-32 items-center justify-center rounded-full bg-black/35 shadow-[inset_0_0_0_2px_rgba(255,255,255,0.2)]">
            <Icon icon="ic:baseline-mic" size={16} className="text-active" />
          </span>
        </div>
        <button
          type="button"
          onClick={request}
          data-test="allow-mic"
          className="bg-active flex h-16 shrink-0 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
          Allow microphone
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <span className="mt-4.5 flex items-center gap-2.5 text-[1.875rem] leading-[1.1] font-bold text-pretty">
        <Icon icon="mdi:warning" size={7.5} className="text-active shrink-0" />
        Access is blocked
      </span>
      <span className="mt-2.5 text-[1rem] leading-[1.45] text-white/85">
        Unblock the microphone in the address bar, then try again.
      </span>
      <div className="flex min-h-0 flex-1 items-center justify-center py-4.5">
        {/* The app's own how-to-unblock clips, rather than the design's placeholder plate. */}
        <div className="flex max-h-70 w-full items-center justify-center overflow-hidden rounded-2xl border-8 border-white bg-[#0f1420] shadow-[0_0.5rem_1.5rem_rgba(0,0,0,0.45)]">
          <MicAccessDeniedView />
        </div>
      </div>
      <button
        type="button"
        onClick={request}
        data-test="retry-mic"
        className="bg-active flex h-16 shrink-0 cursor-pointer items-center justify-center rounded-[1.125rem] border-none text-[1.1875rem] font-bold text-black transition-transform duration-100 active:scale-[0.98]">
        Retry
      </button>
      <button
        type="button"
        onClick={() => setBlocked(false)}
        data-test="mic-blocked"
        className="mt-2.5 flex h-13 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent text-[0.9375rem] text-white/85">
        Ask me again instead
      </button>
    </div>
  );
};

export default StepAllowMic;
